"use client";

import Link from "next/link";
import { memo } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import SizeSelector from "./SizeSelector";
import { useAppSelector } from "@/lib/store";
import Map from "@/assets/icon/mapPinIcon.svg?url";
import styles from "@/assets/css/pages/font.module.css";
import InfoIcon from "@/assets/icon/indo-tooltip-icon.svg";
import ArrowDownNew from "../../Button/SvgIcons/arrowDownNew";
import { htmlEntityToText } from "@/lib/utils/browserMethods";
import PLPStockStatus from "@/components/common/PLPStockStatus";
import ImageWithFallback from "@/components/common/ImageWithFallback";

const Stars = dynamic(() => import("@/components/common/Stars"));
const GuaranteeBadge = dynamic(() => import("../../Badge/GuaranteeBadge"));
const CompatibleBadge = dynamic(() => import("../../Badge/CompatibleBadge"));
const FreeGiftBanner = dynamic(() =>
  import("@/components/common/card/FreeGifts/FreeGiftBanner"),
);

const MemoSizeSelector = memo(SizeSelector);
const MemoPLPStockStatus = memo(PLPStockStatus);

const CenterSection = ({
  t,
  info,
  sizes,
  imageTypes,
  handleLink,
  vehicleInfo,
  detailPageUrl,
  showBranchModal,
  showFitmentInfo,
  hasProductOffer,
  isCompatibleItem,
  handleSizeSelection,
}) => {
  const { showCheckStock } = useAppSelector((state) => state.globalAssets);
  const productFitmentInfo = () => {
    showFitmentInfo({ sku: info.sku, fitment: info.fitment_info });
  };

  return (
    <div className={`plpLeftPart ${hasProductOffer ? "plp-free-gift" : ""}`}>
      <div className="plpTopleftPart">
        <Link
          prefetch={false}
          href={detailPageUrl}
          className="block w-12/12 relative z-10 max-w-max test"
        >
          <h2
            tabIndex={0}
            role="button"
            onClick={handleLink}
            className={`${styles.heading22} heading-22-xs md:max-w-12/12 max-w-[85%] font-semibold`}
          >
            {htmlEntityToText(info?.title_with_brand)}
          </h2>
        </Link>

        <div className="flex flex-row items-center xs:my-2 my-1 justify-between">
          <div className="flex xs:flex-row flex-col xs:items-center items-start">
            {vehicleInfo && isCompatibleItem && (
              <>
                <div className="flex xs:w-max w-12/12 justify-between">
                  <p className="capitalize text-minor-text-1 text-primary-black-500">
                    {t("vehicle_fit", {
                      model: `${vehicleInfo?.make} ${vehicleInfo?.model}`,
                    })}
                  </p>
                </div>
                <span className="mx-1 capitalize text-minor-text-1 text-primary-black-500 xs:inline-block hidden">
                  |
                </span>
              </>
            )}
            <p className="capitalize text-minor-text-1 text-primary-black-500">
              {t("product.part_number")}: {info?.sku}
            </p>
          </div>
          <div className="xs:hidden block">
            {info?.brand?.brand_tier && (
              <span
                className={`radio-badge capitalize ${info?.brand?.brand_tier}`}
              >
                {info?.brand?.brand_tier}
              </span>
            )}
          </div>
        </div>

        {(info?.warranty_guaranteed_text || info?.stop_start_compatible) && (
          <div className="flex xs:flex-row flex-col gap-2 xs:mt-3 mt-1">
            {info?.warranty_guaranteed_text && (
              <GuaranteeBadge title={info?.warranty_guaranteed_text} />
            )}
            {info?.stop_start_compatible && (
              <CompatibleBadge
                title={t("common:product.stop_start_compatible")}
              />
            )}
          </div>
        )}

        {info?.review?.average_rating && (
          <div className="flex items-center mt-2">
            <Stars
              prefix={info?.sku}
              count={info?.review?.num_ratings}
              rating={info?.review?.average_rating}
            />
          </div>
        )}
        <div className="flex justify-between w-12/12 items-center space-x-2 my-0 mt-2 xs:mt-12">
          <div className="flex items-center gap-6 test">
            <div className="flex items-center justify-center">
              <MemoPLPStockStatus
                t={t}
                homeDelivery={info.homeDeliveryStock}
                muteClickCollect={info.muteClickCollect}
              />
            </div>
            {hasProductOffer && (
              <FreeGiftBanner
                variant="plp-freegift"
                label={info?.productOffer?.label}
                icon={info?.productOffer?.promo_image}
              />
            )}
          </div>
        </div>

        <div className="w-12/12 max-w-[322px] xs:pt-12 xs:pb-3 xs:min-h-[67.6px]">
          <button
            type="button"
            onClick={productFitmentInfo}
            className="normalSelect white xs:relative absolute xs:bottom-0 bottom-4 left-0 w-12/12 "
          >
            <div className="fitment-dropdown py-[9px] px-[14px] justify-between flex xs:bg-tertiary-transparent bg-primary-blue-50">
              <div className="flex gap-[6px] items-center">
                <InfoIcon />
                <p className="xs:text-body-text-2 text-minor-text-2 xs:text-primary-black text-primary-blue-900">
                  {t("product-listing:fitment_info")}
                </p>
              </div>
              <ArrowDownNew />
            </div>
          </button>
        </div>
        <MemoSizeSelector
          sizes={sizes}
          selected={info.id}
          handleSizeSelection={handleSizeSelection}
        />
        {showCheckStock && (
          <button
            type="button"
            onClick={() => showBranchModal(info)}
            className="flex items-center xs:mb-2 animation-fadeIn mt-[6px] lg:hidden flex-wrap"
          >
            <div className="w-5">
              <Image
                alt="Map"
                src={Map}
                width={20}
                height={20}
                className="w-auto"
              />
            </div>
            {info?.branch?.name?.length > 0 && (
              <>
                <span className="text-body-text-3-m text-primary-blue-900 m-0 mr-1">
                  {info?.branch.name}{" "}
                </span>
                {info?.stock_in_branch > 0 && info?.stock_in_branch <= 5 && (
                  <span className="text-tertiary-red  text-body-text-3-sb mr-1">
                    {" "}
                    {t("common:product.stock_in_branch", {
                      stock: info?.stock_in_branch,
                    })}
                  </span>
                )}
              </>
            )}
            <span className="text-body-text-3-sb text-primary-blue-900  m-0 underline hover:no-underline cursor-pointer">
              {info?.branch?.name
                ? t("product.delivery_points.change_branch")
                : t("product.delivery_points.check_stock")}
            </span>
          </button>
        )}
      </div>

      <div className="xs:hidden flex w-12/12 max-w-[calc(100%-140px)] items-center ml-[140px] justify-between mt-[6px]">
        <div className="flex items-center">
          <ImageWithFallback
            width={78}
            height={16}
            src={info.brand?.image}
            type={imageTypes.brand}
            alt={info.brand?.title || "brand"}
            key={`${info.brand?.title}_mobile`}
            className="h-[24px] object-contain"
          />
        </div>
        <p className="xl:text-headline-3 sm:text-headline-4 text-body-text-1-b xs:mb-2 mb-0">
          <span
            className={`${info?.showStrikePrice ? "text-primary-red" : ""}`}
          >
            {info?.final_price}
          </span>
          {info?.showStrikePrice && (
            <span className="oldPrice ms-[6px]">{info?.regular_price}</span>
          )}
        </p>
      </div>
    </div>
  );
};

export default CenterSection;
